/**
 * Admin · Operational metrics (phased).
 *
 * Phase 0 stubbed four placeholder sections; Phases 1–4 plug each in by
 * calling `asyncPanel(...)` against a real `/admin/metrics/*` endpoint.
 * Keep the section list in sync with the build plan in
 * `docs/admin-metrics-phases.md`.
 */

import { api } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { asyncPanel, section, statCard } from '../components/metrics.js';
import { toast } from '../components/toast.js';
import { formatKobo } from '../wallet.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// All four phase sections are now real — placeholder list is empty. Kept as
// an explicit "no more deferred sections" landmark for future readers.
const PLACEHOLDER_SECTIONS = [];

// Shared state for the date-range + granularity controls that drive the
// four new feature-area sections (Duels / Power-ups / Friends / Earnings).
// The existing four sections keep their UTC-bucketed defaults and ignore
// these controls (their endpoints don't accept the new params).
const _controlState = {
  start: null, // ISO date string or null = backend default (last 30 Lagos days)
  end: null,
  granularity: 'day', // 'day' | 'week'
};

function _todayIso() {
  // Lagos local "today" approximated as UTC + 1h. Good enough for a
  // date-picker default; the backend canonicalises against Lagos.
  const lagos = new Date(Date.now() + 60 * 60 * 1000);
  return lagos.toISOString().slice(0, 10);
}

function _defaultStartIso() {
  const lagos = new Date(Date.now() + 60 * 60 * 1000 - 29 * 86400 * 1000);
  return lagos.toISOString().slice(0, 10);
}

function _phaseDQuery() {
  const parts = [];
  if (_controlState.start) parts.push(`start=${_controlState.start}`);
  if (_controlState.end) parts.push(`end=${_controlState.end}`);
  if (_controlState.granularity && _controlState.granularity !== 'day') {
    parts.push(`granularity=${_controlState.granularity}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

function _reloadPhaseDSections() {
  const qs = _phaseDQuery();
  asyncPanel(document.getElementById('metrics-duels'), {
    load: () => api(`/admin/metrics/duels${qs}`),
    render: renderDuels,
  });
  asyncPanel(document.getElementById('metrics-powerups'), {
    load: () => api(`/admin/metrics/powerups${qs}`),
    render: renderPowerups,
  });
  asyncPanel(document.getElementById('metrics-friends'), {
    load: () => api(`/admin/metrics/friends${qs}`),
    render: renderFriends,
  });
  asyncPanel(document.getElementById('metrics-earnings'), {
    load: () => api(`/admin/metrics/earnings${qs}`),
    render: renderEarnings,
  });
}

function _renderRangeControls() {
  // Plain-HTML control bar; wired up via onChange listeners after mount.
  const start = _controlState.start || _defaultStartIso();
  const end = _controlState.end || _todayIso();
  return `
    <div class="card" id="metrics-range-controls" style="padding:10px 14px;margin-bottom:14px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;font-size:13px;">
      <span class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;">
        Date range
      </span>
      <input type="date" id="metrics-start" value="${escape(start)}" max="${escape(_todayIso())}"
             style="background:var(--surf-2);color:#fff;border:1px solid #1a1432;border-radius:6px;padding:4px 8px;font-size:12.5px;" />
      <span class="muted">→</span>
      <input type="date" id="metrics-end" value="${escape(end)}" max="${escape(_todayIso())}"
             style="background:var(--surf-2);color:#fff;border:1px solid #1a1432;border-radius:6px;padding:4px 8px;font-size:12.5px;" />
      <span class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-left:8px;">
        Granularity
      </span>
      <select id="metrics-granularity"
              style="background:var(--surf-2);color:#fff;border:1px solid #1a1432;border-radius:6px;padding:4px 8px;font-size:12.5px;">
        <option value="day"${_controlState.granularity === 'day' ? ' selected' : ''}>Day</option>
        <option value="week"${_controlState.granularity === 'week' ? ' selected' : ''}>Week</option>
      </select>
      <button type="button" id="metrics-apply"
              class="btn"
              style="font-size:12px;padding:4px 10px;background:var(--color-accent, #6366f1);color:#fff;border:none;border-radius:6px;cursor:pointer;">
        Apply
      </button>
      <button type="button" id="metrics-reset"
              class="btn"
              style="font-size:12px;padding:4px 10px;background:none;color:var(--color-text-muted);border:1px solid #1a1432;border-radius:6px;cursor:pointer;">
        Reset
      </button>
      <span class="muted" style="font-size:11px;margin-left:auto;">
        Applies to Duels · Power-ups · Friends · Earnings (Africa/Lagos)
      </span>
    </div>
  `;
}

function _wireRangeControls() {
  const apply = document.getElementById('metrics-apply');
  const reset = document.getElementById('metrics-reset');
  const startEl = document.getElementById('metrics-start');
  const endEl = document.getElementById('metrics-end');
  const granEl = document.getElementById('metrics-granularity');
  if (!apply || !reset || !startEl || !endEl || !granEl) return;
  apply.addEventListener('click', () => {
    _controlState.start = startEl.value || null;
    _controlState.end = endEl.value || null;
    _controlState.granularity = granEl.value || 'day';
    _reloadPhaseDSections();
  });
  reset.addEventListener('click', () => {
    _controlState.start = null;
    _controlState.end = null;
    _controlState.granularity = 'day';
    startEl.value = _defaultStartIso();
    endEl.value = _todayIso();
    granEl.value = 'day';
    _reloadPhaseDSections();
  });
}

export async function renderAdminMetrics(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">Metrics (admin)</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/admin" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Metrics</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill bad">ADMIN</span>
    </div>
    <div class="pad" id="metrics-page">
      ${_renderRangeControls()}
      ${section({
        title: 'Financial',
        children: `
          <div id="metrics-recon" data-metrics-section="recon"></div>
          <div id="metrics-financial" data-metrics-section="financial" style="margin-top:14px;"></div>
        `,
      })}
      ${section({
        title: 'Growth & retention',
        children: `<div id="metrics-growth" data-metrics-section="growth"></div>`,
      })}
      ${section({
        title: 'Game health',
        children: `<div id="metrics-game-health" data-metrics-section="game-health"></div>`,
      })}
      ${section({
        title: 'Content quality',
        children: `<div id="metrics-content-quality" data-metrics-section="content-quality"></div>`,
      })}
      ${section({
        title: 'Duels (1v1)',
        children: `<div id="metrics-duels" data-metrics-section="duels"></div>`,
      })}
      ${section({
        title: 'Power-ups',
        children: `<div id="metrics-powerups" data-metrics-section="powerups"></div>`,
      })}
      ${section({
        title: 'Friends & referrals',
        children: `<div id="metrics-friends" data-metrics-section="friends"></div>`,
      })}
      ${section({
        title: 'Earnings',
        children: `<div id="metrics-earnings" data-metrics-section="earnings"></div>`,
      })}
      ${PLACEHOLDER_SECTIONS.map((s) =>
        section({
          title: s.title,
          children: `
            <div data-metrics-section="${s.id}">
              <div class="muted" style="font-size:13px;">${s.summary}</div>
              <div class="muted" style="font-size:11px;margin-top:6px;text-transform:uppercase;letter-spacing:0.06em;">
                ${s.phase} · Coming soon
              </div>
            </div>
          `,
        })
      ).join('')}
    </div>
  `;

  asyncPanel(document.getElementById('metrics-recon'), {
    load: () => api('/admin/metrics/reconciliation'),
    render: renderReconBadge,
  });
  asyncPanel(document.getElementById('metrics-financial'), {
    load: () => api('/admin/metrics/financial'),
    render: renderFinancial,
  });
  asyncPanel(document.getElementById('metrics-growth'), {
    load: () => api('/admin/metrics/growth'),
    render: renderGrowth,
  });
  asyncPanel(document.getElementById('metrics-game-health'), {
    load: () => api('/admin/metrics/game-health'),
    render: renderGameHealth,
  });
  asyncPanel(document.getElementById('metrics-content-quality'), {
    load: () => api('/admin/metrics/content-quality'),
    render: renderContentQuality,
  });
  // Phase C/D — duels / power-ups / friends / earnings. The shared
  // date-range + granularity controls at the top of the page drive
  // these four; Apply / Reset re-fetches them. CSV downloads need an
  // auth'd fetch (Bearer header), not a bare <a href>, so each
  // section renders a button and we delegate the click here.
  const pageRoot = document.getElementById('metrics-page');
  if (pageRoot) {
    pageRoot.addEventListener('click', onCsvClick);
  }
  _wireRangeControls();
  _reloadPhaseDSections();
}

// ---------------------------------------------------------------------------
// Reconciliation badge
// ---------------------------------------------------------------------------

function renderReconBadge(r) {
  const ok = r.ok === true;
  const tone = ok ? 'ok' : 'danger';
  const headline = ok ? 'Recon OK' : 'RECON DRIFT';
  const detail = ok
    ? `${r.accounts_checked} accounts · ${r.txns_checked} txns balanced · ledger Σ ${formatKobo(
        r.global_dr_kobo
      )}`
    : reconProblems(r);
  const checked = r.checked_at ? new Date(r.checked_at).toLocaleString('en-NG') : '';
  return `
    <div style="padding:14px;border-radius:10px;background:${
      ok ? 'var(--color-success-soft)' : 'var(--color-danger-soft)'
    };border-left:4px solid var(${ok ? '--color-success' : '--color-danger'});">
      <div class="row" style="justify-content:space-between;align-items:flex-start;gap:8px;">
        <div>
          <div style="font-family:var(--font-display);font-size:18px;font-weight:700;color:var(--color-${
            tone === 'ok' ? 'success' : 'danger'
          });">
            ${escape(headline)}
          </div>
          <div style="font-size:12.5px;color:#fff;margin-top:4px;">${detail}</div>
        </div>
        <div class="muted" style="font-size:10.5px;white-space:nowrap;">checked ${escape(checked)}</div>
      </div>
      ${
        !ok && r.account_drift && r.account_drift.length
          ? `<div style="margin-top:10px;padding:8px;background:#0E0B1F;border-radius:8px;font-size:11.5px;">
              ${r.account_drift
                .map(
                  (d) =>
                    `<div class="row" style="justify-content:space-between;gap:8px;">
                      <span class="muted">${escape(d.account_type)} · ${escape(d.account_id.slice(0, 8))}</span>
                      <span style="color:#fff;">cache ${escape(formatKobo(d.cached_kobo))} vs ledger ${escape(formatKobo(d.derived_kobo))}</span>
                    </div>`
                )
                .join('')}
            </div>`
          : ''
      }
    </div>
  `;
}

function reconProblems(r) {
  const parts = [];
  if (r.unbalanced_txns_count) {
    parts.push(`${r.unbalanced_txns_count} unbalanced txn${r.unbalanced_txns_count === 1 ? '' : 's'}`);
  }
  if (r.account_drift_count) {
    parts.push(`${r.account_drift_count} account drift${r.account_drift_count === 1 ? '' : 's'}`);
  }
  if (r.global_diff_kobo) {
    parts.push(`global Σ off by ${formatKobo(Math.abs(r.global_diff_kobo))}`);
  }
  return parts.join(' · ') || 'Drift detected — check server logs.';
}

// ---------------------------------------------------------------------------
// Financial snapshot
// ---------------------------------------------------------------------------

function renderFinancial(f) {
  const aging = f.pending_withdrawal_aging.buckets;
  const oldestHours = f.pending_withdrawal_aging.oldest_age_hours;
  const oldestNote =
    oldestHours == null
      ? 'No pending withdrawals.'
      : `Oldest pending: ${formatHours(oldestHours)}`;
  return `
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({ label: 'Platform revenue', value: formatKobo(f.platform_revenue_kobo) })}
      ${statCard({ label: 'Wallet liability', value: formatKobo(f.wallet_liability_kobo) })}
      ${statCard({
        label: 'Net flow (30d)',
        value: formatKobo(f.net_flow_kobo),
        tone: f.net_flow_kobo < 0 ? 'danger' : undefined,
      })}
      ${statCard({ label: 'Lifetime rake', value: formatKobo(f.rake_lifetime_kobo) })}
    </div>

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Deposits (30d)
    </h4>
    ${renderDepositTable(f.deposits)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Withdrawals (30d)
    </h4>
    ${renderWithdrawTable(f.withdrawals.totals)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Pending withdrawal aging
    </h4>
    <div class="grid cols-3" style="gap:10px;">
      ${statCard({ label: '< 1 day', value: aging.lt_1d })}
      ${statCard({ label: '1–7 days', value: aging['1d_to_7d'] })}
      ${statCard({
        label: '> 7 days',
        value: aging.gt_7d,
        tone: aging.gt_7d > 0 ? 'danger' : undefined,
      })}
    </div>
    <div class="muted" style="font-size:11px;margin-top:6px;">${escape(oldestNote)}</div>

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Payouts split (30d)
    </h4>
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'Sponsor-funded live',
        value: formatKobo(f.payouts.live_payout_kobo),
        sub: 'from platform revenue',
      })}
      ${statCard({
        label: 'Paid contests',
        value: formatKobo(f.payouts.contest_settle_winner_kobo),
        sub: 'from prize pool',
      })}
    </div>

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Daily flow (30d)
    </h4>
    ${renderDailyFlow(f.daily_flow)}
  `;
}

function renderDepositTable(d) {
  const t = d.totals;
  if (!d.by_provider.length) {
    return `<div class="muted" style="font-size:12px;">No deposits in the window.</div>`;
  }
  return `
    <div style="display:grid;gap:6px;">
      ${d.by_provider
        .map(
          (row) => `
            <div class="row" style="justify-content:space-between;font-size:13px;padding:6px 0;border-bottom:1px solid #1a1432;">
              <span style="color:#fff;text-transform:capitalize;">${escape(row.provider)}</span>
              <span class="muted" style="font-size:11px;">
                ${row.succeeded} ok · ${row.pending} pending · ${row.failed} failed
              </span>
              <span style="color:#fff;font-family:var(--font-display);">${escape(formatKobo(row.succeeded_kobo))}</span>
            </div>
          `
        )
        .join('')}
      <div class="row" style="justify-content:space-between;font-size:12.5px;padding-top:6px;">
        <span class="muted">Total succeeded</span>
        <span style="color:#fff;font-family:var(--font-display);">${escape(formatKobo(t.succeeded_kobo))}</span>
      </div>
    </div>
  `;
}

function renderWithdrawTable(t) {
  return `
    <div style="display:grid;gap:6px;font-size:13px;">
      <div class="row" style="justify-content:space-between;">
        <span class="muted">Settled</span>
        <span style="color:#fff;">${t.succeeded} · ${escape(formatKobo(t.succeeded_kobo))}</span>
      </div>
      <div class="row" style="justify-content:space-between;">
        <span class="muted">Pending</span>
        <span style="color:#fff;">${t.pending}</span>
      </div>
      <div class="row" style="justify-content:space-between;">
        <span class="muted">Failed / cancelled</span>
        <span style="color:#fff;">${t.failed + t.cancelled}</span>
      </div>
    </div>
  `;
}

function renderDailyFlow(rows) {
  if (!rows.length) return `<div class="muted" style="font-size:12px;">No activity yet.</div>`;
  // Bar chart: each day gets a vertical bar of net_kobo, scaled to the max
  // absolute value in the series so positive and negative days remain
  // visible. Hover/tap reveals the precise day numbers via title attr.
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.net_kobo)));
  const formatDay = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
  };
  return `
    <div style="display:flex;gap:2px;align-items:flex-end;height:80px;padding:6px 0;">
      ${rows
        .map((r) => {
          const pct = Math.round((Math.abs(r.net_kobo) / maxAbs) * 100);
          const tone = r.net_kobo < 0 ? 'var(--color-danger)' : 'var(--color-success)';
          const title = `${formatDay(r.date)} · net ${formatKobo(r.net_kobo)} (in ${formatKobo(r.deposits_kobo)} / out ${formatKobo(r.withdrawals_kobo)})`;
          return `<div title="${escape(title)}" style="flex:1;background:${tone};opacity:0.85;height:${pct}%;min-height:1px;border-radius:2px;"></div>`;
        })
        .join('')}
    </div>
    <div class="row" style="justify-content:space-between;font-size:10.5px;color:var(--color-text-muted);margin-top:2px;">
      <span>${escape(formatDay(rows[0].date))}</span>
      <span>${escape(formatDay(rows[rows.length - 1].date))}</span>
    </div>
  `;
}

function formatHours(h) {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d`;
}

// ---------------------------------------------------------------------------
// Growth & retention
// ---------------------------------------------------------------------------

function renderGrowth(g) {
  return `
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'DAU / MAU',
        value: formatRatio(g.stickiness.ratio),
        sub: `${g.stickiness.dau.toLocaleString('en-NG')} / ${g.stickiness.mau.toLocaleString('en-NG')}`,
      })}
      ${statCard({
        label: 'Email verified',
        value: formatRatio(g.verification.rate),
        sub: `${g.verification.verified.toLocaleString('en-NG')} of ${g.verification.total.toLocaleString('en-NG')}`,
      })}
      ${statCard({
        label: 'Social linked',
        value: formatRatio(g.social_adoption.rate),
        sub: `${g.social_adoption.linked_users.toLocaleString('en-NG')} of ${g.social_adoption.total_users.toLocaleString('en-NG')}`,
      })}
      ${statCard({
        label: 'Total users',
        value: g.social_adoption.total_users.toLocaleString('en-NG'),
      })}
    </div>

    ${renderSocialBreakdown(g.social_adoption.by_provider)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Signups (30d)
    </h4>
    ${renderSignupsBars(g.signups_daily)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Retention cohorts (last 8 weeks)
    </h4>
    ${renderCohortTable(g.retention_cohorts)}
    <div class="muted" style="font-size:10.5px;margin-top:6px;">${escape(g.last_seen_caveat || '')}</div>
  `;
}

function renderSocialBreakdown(rows) {
  if (!rows || !rows.length) {
    return `<div class="muted" style="font-size:11px;margin-top:6px;">No social logins yet.</div>`;
  }
  return `
    <div class="row" style="gap:10px;flex-wrap:wrap;font-size:11.5px;margin-top:8px;color:var(--color-text-muted);">
      ${rows
        .map(
          (r) =>
            `<span><span style="color:#fff;text-transform:capitalize;">${escape(r.provider)}</span> · ${r.linked_users.toLocaleString('en-NG')}</span>`
        )
        .join(' · ')}
    </div>
  `;
}

function renderSignupsBars(rows) {
  if (!rows.length) return `<div class="muted" style="font-size:12px;">No signups in the window.</div>`;
  const max = Math.max(1, ...rows.map((r) => r.count));
  const formatDay = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
  };
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  return `
    <div style="display:flex;gap:2px;align-items:flex-end;height:80px;padding:6px 0;">
      ${rows
        .map((r) => {
          const pct = Math.round((r.count / max) * 100);
          const title = `${formatDay(r.date)} · ${r.count} signup${r.count === 1 ? '' : 's'}`;
          return `<div title="${escape(title)}" style="flex:1;background:var(--color-success);opacity:0.85;height:${pct}%;min-height:1px;border-radius:2px;"></div>`;
        })
        .join('')}
    </div>
    <div class="row" style="justify-content:space-between;font-size:10.5px;color:var(--color-text-muted);margin-top:2px;">
      <span>${escape(formatDay(rows[0].date))}</span>
      <span>${total.toLocaleString('en-NG')} total · last ${rows.length}d</span>
      <span>${escape(formatDay(rows[rows.length - 1].date))}</span>
    </div>
  `;
}

function renderCohortTable(cohorts) {
  if (!cohorts || !cohorts.length) {
    return `<div class="muted" style="font-size:12px;">No cohort data yet.</div>`;
  }
  const headerCell = (txt) =>
    `<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:var(--color-text-muted);text-align:right;">${escape(txt)}</div>`;
  const dataCell = (txt, opts = {}) =>
    `<div style="font-size:12.5px;text-align:right;color:${opts.muted ? 'var(--color-text-muted)' : '#fff'};font-family:${opts.mono ? 'var(--font-display)' : 'inherit'};">${escape(txt)}</div>`;
  const rows = cohorts
    .slice()
    .reverse() // newest at the top — what an admin actually wants to scan
    .map((c) => {
      const startDate = new Date(`${c.cohort_start}T00:00:00Z`);
      const startLabel = startDate.toLocaleDateString('en-NG', {
        month: 'short',
        day: 'numeric',
      });
      return `
        <div style="display:grid;grid-template-columns:1.2fr 0.6fr 0.7fr 0.7fr 0.7fr;gap:6px;padding:6px 0;border-bottom:1px solid #1a1432;">
          <div style="font-size:12.5px;color:#fff;">Wk of ${escape(startLabel)}</div>
          ${dataCell(String(c.size), { mono: true })}
          ${renderCohortCell(c.d1)}
          ${renderCohortCell(c.d7)}
          ${renderCohortCell(c.d30)}
        </div>
      `;
    })
    .join('');
  return `
    <div style="background:var(--surf-2);border-radius:8px;padding:10px 12px;">
      <div style="display:grid;grid-template-columns:1.2fr 0.6fr 0.7fr 0.7fr 0.7fr;gap:6px;padding-bottom:4px;border-bottom:1px solid #1a1432;">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:var(--color-text-muted);">Cohort</div>
        ${headerCell('Size')}
        ${headerCell('D1')}
        ${headerCell('D7')}
        ${headerCell('D30')}
      </div>
      ${rows}
    </div>
  `;
}

function renderCohortCell(cell) {
  if (cell.rate === null) {
    return `<div style="font-size:12.5px;text-align:right;color:var(--color-text-muted);">—</div>`;
  }
  const pct = Math.round(cell.rate * 100);
  const tone = pct >= 30 ? 'var(--color-success)' : pct >= 10 ? '#fff' : 'var(--color-danger)';
  return `
    <div style="font-size:12.5px;text-align:right;color:${tone};">
      ${pct}<span style="opacity:0.6;font-size:10.5px;">%</span>
      <div style="font-size:10px;color:var(--color-text-muted);">${cell.active}</div>
    </div>
  `;
}

function formatRatio(r) {
  if (r === null || r === undefined) return '—';
  return `${Math.round(r * 1000) / 10}%`;
}

// ---------------------------------------------------------------------------
// Game health
// ---------------------------------------------------------------------------

function renderGameHealth(g) {
  return `
    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:0 0 8px;">
      Live trivia funnel (recent)
    </h4>
    ${renderFunnel(g.live_trivias_funnel)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Question accuracy outliers
    </h4>
    ${renderOutliers(g.question_accuracy_outliers)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Answer-position distribution
    </h4>
    ${renderPositions(g.answer_position_distribution)}
  `;
}

function renderFunnel(rows) {
  if (!rows || !rows.length) {
    return `<div class="muted" style="font-size:12px;">No live or finished trivias in the window.</div>`;
  }
  return `
    <div style="display:grid;gap:8px;">
      ${rows.map(renderFunnelRow).join('')}
    </div>
  `;
}

function renderFunnelRow(r) {
  const isLive = r.status === 'live';
  const r2j = r.drop_off.register_to_join;
  const j2c = r.drop_off.join_to_complete;
  const when = r.started_at
    ? new Date(r.started_at).toLocaleString('en-NG', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
  return `
    <a href="/admin/live" data-link style="display:block;text-decoration:none;color:inherit;padding:10px 12px;background:var(--surf-2);border-radius:8px;border-left:3px solid ${isLive ? 'var(--color-success)' : '#1a1432'};">
      <div class="row" style="justify-content:space-between;align-items:flex-start;gap:8px;">
        <div style="min-width:0;flex:1;">
          <div style="font-size:13px;color:#fff;font-weight:600;">
            ${escape(r.title)}
            ${isLive ? '<span class="status-pill bad" style="margin-left:6px;">LIVE</span>' : ''}
          </div>
          <div class="muted" style="font-size:11px;margin-top:2px;">${escape(when)}</div>
        </div>
        <div class="row" style="gap:14px;font-size:12px;color:#fff;">
          <span>${r.registered}<span class="muted" style="font-size:10px;display:block;text-align:center;">REG</span></span>
          <span>${r.joined}<span class="muted" style="font-size:10px;display:block;text-align:center;">JOIN</span></span>
          <span>${r.completed}<span class="muted" style="font-size:10px;display:block;text-align:center;">DONE</span></span>
        </div>
      </div>
      <div class="row" style="gap:14px;font-size:11px;color:var(--color-text-muted);margin-top:6px;">
        <span>Reg→Join drop: ${dropOffLabel(r2j)}</span>
        <span>Join→Done drop: ${dropOffLabel(j2c)}</span>
      </div>
    </a>
  `;
}

function dropOffLabel(d) {
  if (d === null || d === undefined) return '—';
  const pct = Math.round(d * 100);
  const tone = pct >= 50 ? 'var(--color-danger)' : '#fff';
  return `<span style="color:${tone};">${pct}%</span>`;
}

function renderOutliers(o) {
  const empty =
    (!o.too_hard || !o.too_hard.length) && (!o.too_easy || !o.too_easy.length);
  if (empty) {
    return `<div class="muted" style="font-size:12px;">No outliers yet — needs ≥${o.min_answers} answers per question to flag.</div>`;
  }
  return `
    <div class="grid cols-2" style="gap:10px;align-items:start;">
      <div>
        <div style="font-size:11.5px;color:var(--color-danger);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;">
          Too hard (≤${Math.round(o.too_hard_rate_threshold * 100)}% correct)
        </div>
        ${renderOutlierList(o.too_hard, 'danger')}
      </div>
      <div>
        <div style="font-size:11.5px;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;">
          Too easy (≥${Math.round(o.too_easy_rate_threshold * 100)}% correct)
        </div>
        ${renderOutlierList(o.too_easy, 'muted')}
      </div>
    </div>
    <div class="muted" style="font-size:10.5px;margin-top:8px;">
      Min ${o.min_answers} answers per question · open <a href="/admin/questions" data-link>questions list</a> and search by id.
    </div>
  `;
}

function renderOutlierList(rows, tone) {
  if (!rows.length) {
    return `<div class="muted" style="font-size:11px;">None.</div>`;
  }
  const accentColor = tone === 'danger' ? 'var(--color-danger)' : '#fff';
  return `
    <div style="display:grid;gap:6px;">
      ${rows
        .map(
          (r) => `
            <div style="padding:8px;background:var(--surf-2);border-radius:6px;">
              <div style="font-size:12px;color:#fff;line-height:1.35;">
                ${escape(r.prompt_preview || '(no prompt)')}
              </div>
              <div class="row" style="justify-content:space-between;font-size:10.5px;margin-top:4px;">
                <span class="muted">${escape(r.question_id.slice(0, 8))}…</span>
                <span style="color:${accentColor};">${Math.round(r.correct_rate * 100)}% · ${r.answers}n</span>
              </div>
            </div>
          `
        )
        .join('')}
    </div>
  `;
}

function renderPositions(p) {
  const slot = (label, b) => {
    const total = b.total || 1;
    return `
      <div>
        <div class="muted" style="font-size:10.5px;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px;">
          ${escape(label)} · ${b.total.toLocaleString('en-NG')}n
        </div>
        <div class="row" style="gap:4px;align-items:flex-end;height:60px;">
          ${[0, 1, 2, 3]
            .map((i) => {
              const count = b[String(i)] || 0;
              const pct = Math.round((count / total) * 100);
              const barH = Math.max(2, Math.round((count / Math.max(1, b.total)) * 60));
              const tone = Math.abs(pct - 25) >= 10 ? 'var(--color-danger)' : 'var(--color-success)';
              const slotLabel = ['A', 'B', 'C', 'D'][i];
              return `
                <div style="flex:1;text-align:center;">
                  <div title="${slotLabel}: ${count} (${pct}%)" style="background:${tone};opacity:0.85;height:${barH}px;border-radius:3px;"></div>
                  <div style="font-size:10px;color:var(--color-text-muted);margin-top:3px;">${slotLabel} · ${pct}%</div>
                </div>
              `;
            })
            .join('')}
        </div>
      </div>
    `;
  };
  return `
    <div class="grid cols-2" style="gap:14px;align-items:start;">
      ${slot('All clicks', p.all_clicks)}
      ${slot('Correct clicks', p.correct_clicks)}
    </div>
    <div class="muted" style="font-size:10.5px;margin-top:8px;">${escape(p.note || '')}</div>
  `;
}

// ---------------------------------------------------------------------------
// Content quality
// ---------------------------------------------------------------------------

function renderContentQuality(c) {
  return `
    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:0 0 8px;">
      Difficulty mix (published)
    </h4>
    ${renderDifficultyMix(c.difficulty_mix)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Open question flags
    </h4>
    ${renderFlags(c.open_flags)}

    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Fatigue (30d)
    </h4>
    ${renderFatigue(c.fatigue)}
  `;
}

const DIFF_COLOR = {
  easy: 'var(--color-success)',
  medium: 'var(--gold, #E5A630)',
  hard: 'var(--color-danger)',
};

function renderDifficultyMix(d) {
  const totalPublished = d.totals.easy + d.totals.medium + d.totals.hard;
  if (totalPublished === 0) {
    return `<div class="muted" style="font-size:12px;">No published questions yet.</div>`;
  }
  return `
    ${renderDifficultyBar(d.totals, totalPublished)}
    <div class="row" style="gap:14px;flex-wrap:wrap;font-size:11.5px;margin-top:6px;color:var(--color-text-muted);">
      <span><b style="color:${DIFF_COLOR.easy};">Easy</b> ${d.totals.easy.toLocaleString('en-NG')}</span>
      <span><b style="color:${DIFF_COLOR.medium};">Medium</b> ${d.totals.medium.toLocaleString('en-NG')}</span>
      <span><b style="color:${DIFF_COLOR.hard};">Hard</b> ${d.totals.hard.toLocaleString('en-NG')}</span>
      <span class="muted">${totalPublished.toLocaleString('en-NG')} total</span>
    </div>
    ${renderDifficultyTable(d.by_category)}
  `;
}

function renderDifficultyBar(t, total) {
  const pct = (n) => (n / total) * 100;
  return `
    <div style="display:flex;height:10px;border-radius:5px;overflow:hidden;background:var(--surf-2);">
      <div title="Easy ${t.easy}" style="width:${pct(t.easy)}%;background:${DIFF_COLOR.easy};"></div>
      <div title="Medium ${t.medium}" style="width:${pct(t.medium)}%;background:${DIFF_COLOR.medium};"></div>
      <div title="Hard ${t.hard}" style="width:${pct(t.hard)}%;background:${DIFF_COLOR.hard};"></div>
    </div>
  `;
}

function renderDifficultyTable(rows) {
  if (!rows || !rows.length) return '';
  return `
    <div style="background:var(--surf-2);border-radius:8px;padding:10px 12px;margin-top:12px;">
      <div style="display:grid;grid-template-columns:1.5fr 0.6fr 0.6fr 0.6fr 0.6fr;gap:6px;padding-bottom:4px;border-bottom:1px solid #1a1432;">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:var(--color-text-muted);">Category</div>
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:var(--color-text-muted);text-align:right;">Easy</div>
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:var(--color-text-muted);text-align:right;">Med</div>
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:var(--color-text-muted);text-align:right;">Hard</div>
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.06em;color:var(--color-text-muted);text-align:right;">Σ</div>
      </div>
      ${rows
        .map(
          (r) => `
            <div style="display:grid;grid-template-columns:1.5fr 0.6fr 0.6fr 0.6fr 0.6fr;gap:6px;padding:5px 0;border-bottom:1px solid #1a1432;font-size:12.5px;">
              <div style="color:#fff;">${escape(r.category_name || r.slug || '(unnamed)')}</div>
              <div style="text-align:right;color:${DIFF_COLOR.easy};">${r.easy}</div>
              <div style="text-align:right;color:${DIFF_COLOR.medium};">${r.medium}</div>
              <div style="text-align:right;color:${DIFF_COLOR.hard};">${r.hard}</div>
              <div style="text-align:right;color:#fff;">${r.total}</div>
            </div>
          `
        )
        .join('')}
    </div>
  `;
}

function renderFlags(f) {
  if (!f.count) {
    return `<div class="muted" style="font-size:12px;">No flagged questions.</div>`;
  }
  return `
    <div style="padding:10px 12px;background:${f.count > 0 ? 'var(--color-danger-soft)' : 'var(--surf-2)'};border-left:3px solid var(--color-danger);border-radius:6px;font-size:13px;">
      <b style="color:var(--color-danger);">${f.count.toLocaleString('en-NG')} flag${f.count === 1 ? '' : 's'}</b>
      <span class="muted" style="margin-left:6px;">awaiting review</span>
    </div>
    ${f.recent && f.recent.length ? `
      <div style="display:grid;gap:6px;margin-top:8px;">
        ${f.recent.map(renderFlagRow).join('')}
      </div>
    ` : ''}
    <div class="muted" style="font-size:10.5px;margin-top:6px;">
      ${escape(f.note || '')} · Open <a href="/admin/questions" data-link>questions</a> to act.
    </div>
  `;
}

function renderFlagRow(r) {
  const when = r.created_at ? formatAgo(r.created_at) : '';
  return `
    <div style="padding:8px 10px;background:var(--surf-2);border-radius:6px;">
      <div style="font-size:12.5px;color:#fff;line-height:1.35;">${escape(r.prompt_preview || '(no prompt)')}</div>
      <div class="row" style="justify-content:space-between;font-size:10.5px;margin-top:4px;">
        <span class="muted">${escape(r.question_id.slice(0, 8))}… · ${escape(when)}</span>
        ${r.reason ? `<span style="color:#fff;">${escape(r.reason)}</span>` : '<span class="muted">no reason</span>'}
      </div>
    </div>
  `;
}

function renderFatigue(f) {
  return `
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'Served in 30d',
        value: formatRatio(f.rate),
        sub: `${f.served_in_window.toLocaleString('en-NG')} of ${f.published_total.toLocaleString('en-NG')}`,
        tone: f.rate !== null && f.rate < 0.3 ? 'danger' : undefined,
      })}
      ${statCard({
        label: 'Never served',
        value: f.never_served_count.toLocaleString('en-NG'),
        sub: 'published, times_served = 0',
        tone: f.never_served_count > Math.max(10, f.published_total * 0.3) ? 'danger' : undefined,
      })}
      ${statCard({
        label: 'Median times served',
        value: f.times_served_median.toLocaleString('en-NG'),
      })}
      ${statCard({
        label: 'Max times served',
        value: f.times_served_max.toLocaleString('en-NG'),
      })}
    </div>
    <div class="muted" style="font-size:10.5px;margin-top:6px;">
      "Served in 30d" counts distinct questions surfaced in any LiveTriviaAnswer in the window — solo/duel exposure not yet tracked here.
    </div>
  `;
}

function formatAgo(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const sec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Phase C — Duels / Power-ups / Friends / Earnings
//
// All four sections share the same response shape: {range, totals,
// timeseries[]}. Below: section-specific stat tiles + a hand-rolled
// trend bar (same SVG-free pattern as renderDailyFlow above).
// ---------------------------------------------------------------------------

function formatPercentBps(bps) {
  // The Phase C endpoints return rates as integer basis points so the
  // schema stays integer-only; divide by 100 for display.
  if (bps === null || bps === undefined) return '—';
  return `${(bps / 100).toFixed(1)}%`;
}

function csvButton(endpoint, range, filename) {
  const params = [`start=${range.start}`, `end=${range.end}`, 'format=csv'];
  if (range.granularity && range.granularity !== 'day') {
    params.push(`granularity=${range.granularity}`);
  }
  const url = `${endpoint}?${params.join('&')}`;
  return `
    <button type="button" class="btn btn-ghost" style="font-size:11px;padding:4px 8px;text-decoration:underline;background:none;border:none;cursor:pointer;color:var(--color-text-muted);"
            data-csv-url="${escape(url)}" data-csv-filename="${escape(filename)}">
      Export CSV
    </button>
  `;
}

function rangeCaption(range) {
  return `${range.start} → ${range.end} · Africa/Lagos`;
}

function rangeHeader(range, endpoint, filenameStem) {
  const filename = `${filenameStem}_${range.start}_${range.end}.csv`;
  return `
    <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:10px;">
      <div class="muted" style="font-size:11.5px;">${escape(rangeCaption(range))}</div>
      ${csvButton(endpoint, range, filename)}
    </div>
  `;
}

async function onCsvClick(ev) {
  const btn = ev.target.closest('[data-csv-url]');
  if (!btn) return;
  ev.preventDefault();
  const url = btn.getAttribute('data-csv-url');
  const filename = btn.getAttribute('data-csv-filename') || 'metrics.csv';
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  const token = auth.getAccessToken();
  try {
    const res = await fetch(fullUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) {
      toast.error(`CSV export failed (${res.status}).`);
      return;
    }
    const text = await res.text();
    const blob = new Blob([text], { type: 'text/csv' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    toast.fromError(err);
  }
}

function miniTrend(rows, key, { tone = 'fg', formatter, label } = {}) {
  if (!rows || !rows.length) {
    return `<div class="muted" style="font-size:12px;">No activity in the window.</div>`;
  }
  const values = rows.map((r) => Number(r[key] || 0));
  const maxV = Math.max(1, ...values);
  const fmt = formatter || ((v) => v.toLocaleString('en-NG'));
  const formatDay = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
  };
  const colorVar = tone === 'money' ? 'var(--color-success)' : 'var(--color-accent, #6366f1)';
  return `
    ${label ? `<div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px;">${escape(label)}</div>` : ''}
    <div style="display:flex;gap:2px;align-items:flex-end;height:64px;padding:4px 0;">
      ${values
        .map((v, i) => {
          const pct = Math.round((v / maxV) * 100);
          const title = `${formatDay(rows[i].date)} · ${fmt(v)}`;
          return `<div title="${escape(title)}" style="flex:1;background:${colorVar};opacity:${v ? 0.85 : 0.2};height:${Math.max(pct, 1)}%;min-height:1px;border-radius:2px;"></div>`;
        })
        .join('')}
    </div>
    <div class="row" style="justify-content:space-between;font-size:10.5px;color:var(--color-text-muted);margin-top:2px;">
      <span>${escape(formatDay(rows[0].date))}</span>
      <span>${escape(formatDay(rows[rows.length - 1].date))}</span>
    </div>
  `;
}

// Friendly column labels for the per-section data tables. Keys not in
// this map fall back to the raw key with underscores → spaces.
const COL_LABELS = {
  date: 'Date',
  duels_created: 'Created',
  duels_accepted: 'Accepted',
  duels_completed: 'Completed',
  duels_abandoned: 'Abandoned',
  duels_declined: 'Declined',
  duels_paid_count: 'Paid',
  duels_free_count: 'Free',
  duel_avg_stake_kobo: 'Avg stake',
  duel_avg_matchmaking_wait_seconds: 'Avg wait (s)',
  friend_duels_completed: 'Friend duels',
  eraser_units_sold: 'Erasers sold',
  eraser_revenue_kobo: 'Eraser revenue',
  erasers_used_total: 'Erasers used',
  fiftyfifty_used_total: '50/50 used',
  fiftyfifty_granted_referral: '50/50 (ref)',
  fiftyfifty_granted_awarded: '50/50 (rank)',
  fiftyfifty_granted_admin: '50/50 (admin)',
  eraser_arm_rate_bps: 'Arm rate',
  eraser_use_rate_bps: 'Use rate',
  avg_fiftyfifty_per_live_game_x100: 'Avg 50/50/game',
  powerup_holders_eraser_bps: 'Eraser holders %',
  powerup_holders_fiftyfifty_bps: '50/50 holders %',
  referrals_joined: 'Joined',
  referrals_rewarded: 'Rewarded',
  friendships_accepted: 'Friend ✓',
  friend_requests_sent: 'Friend req',
  duel_rake_kobo: 'Rake',
  duel_handle_kobo: 'Handle',
  duel_prizes_paid_kobo: 'Prizes',
  payment_fees_kobo: 'Fees',
  paying_players: 'Paying',
  active_players: 'Active',
};

function columnLabel(key) {
  return COL_LABELS[key] || key.replace(/_/g, ' ');
}

function formatCell(key, value) {
  if (value === null || value === undefined) return '—';
  if (key === 'date') {
    const d = new Date(`${value}T00:00:00Z`);
    return d.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
  }
  if (key.endsWith('_kobo')) return formatKobo(value);
  if (key.endsWith('_bps')) return formatPercentBps(value);
  if (key.endsWith('_x100')) return (value / 100).toFixed(2);
  return Number(value).toLocaleString('en-NG');
}

/**
 * Render a small table beneath a section's chart. `columns` is the
 * ordered list of keys (including 'date') to show. Rows are the
 * timeseries entries straight from the endpoint payload. We cap the
 * visible rows to keep the section compact; admins can hit Export CSV
 * for the full window.
 */
function dataTable(rows, columns, { maxRows = 14 } = {}) {
  if (!rows || !rows.length) return '';
  const shown = rows.slice(-maxRows).reverse(); // most recent first
  const head = columns
    .map(
      (c) =>
        `<th style="text-align:${c === 'date' ? 'left' : 'right'};padding:4px 6px;color:var(--color-text-muted);font-weight:500;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;">${escape(columnLabel(c))}</th>`
    )
    .join('');
  const body = shown
    .map((row) => {
      const tds = columns
        .map(
          (c) =>
            `<td style="text-align:${c === 'date' ? 'left' : 'right'};padding:4px 6px;font-size:12px;color:#fff;">${escape(formatCell(c, row[c]))}</td>`
        )
        .join('');
      return `<tr>${tds}</tr>`;
    })
    .join('');
  const more =
    rows.length > maxRows
      ? `<div class="muted" style="font-size:11px;margin-top:4px;text-align:right;">Showing latest ${maxRows} of ${rows.length} — full window in CSV export.</div>`
      : '';
  return `
    <div style="margin-top:12px;overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
      ${more}
    </div>
  `;
}

function renderDuels(d) {
  const t = d.totals;
  return `
    ${rangeHeader(d.range, '/admin/metrics/duels', 'duels')}
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({ label: 'Created', value: (t.duels_created || 0).toLocaleString('en-NG') })}
      ${statCard({ label: 'Accepted', value: (t.duels_accepted || 0).toLocaleString('en-NG') })}
      ${statCard({ label: 'Completed', value: (t.duels_completed || 0).toLocaleString('en-NG') })}
      ${statCard({
        label: 'Accept rate',
        value: formatPercentBps(t.accept_rate_bps),
        sub: `${t.duels_accepted || 0} / ${t.duels_created || 0}`,
      })}
      ${statCard({
        label: 'Abandon rate',
        value: formatPercentBps(t.abandon_rate_bps),
        tone: (t.abandon_rate_bps || 0) > 2000 ? 'danger' : undefined,
        sub: `${(t.duels_declined || 0) + (t.duels_abandoned || 0)} / ${t.duels_created || 0}`,
      })}
      ${statCard({
        label: 'Avg stake (paid)',
        value: formatKobo(t.duel_avg_stake_kobo || 0),
      })}
      ${statCard({
        label: 'Avg matchmaking wait',
        value: `${(t.duel_avg_matchmaking_wait_seconds || 0).toLocaleString('en-NG')}s`,
        sub: 'invite-driven duels only',
      })}
      ${statCard({
        label: 'Paid vs free',
        value: `${formatPercentBps(t.paid_share_bps)} paid`,
        sub: `${t.duels_paid_count || 0} paid · ${t.duels_free_count || 0} free`,
      })}
      ${statCard({
        label: 'Friend duel share',
        value: formatPercentBps(t.friend_duel_share_bps),
        sub: `${t.friend_duels_completed || 0} / ${t.duels_completed || 0}`,
      })}
      ${statCard({
        label: 'Duels / active player',
        value: ((t.duels_per_active_player_x100 || 0) / 100).toFixed(2),
        sub: `${t.active_players || 0} active players`,
      })}
    </div>
    <div style="margin-top:14px;">
      ${miniTrend(d.timeseries, 'duels_completed', { label: 'Duels completed' })}
    </div>
    ${dataTable(d.timeseries, [
      'date',
      'duels_created',
      'duels_accepted',
      'duels_completed',
      'duels_abandoned',
      'duels_paid_count',
      'duel_avg_stake_kobo',
    ])}
  `;
}

function renderPowerups(p) {
  const t = p.totals;
  // Strip the `eraser_`/`ff_` SKU prefix so the inline list reads as
  // "1× 5 · 3× 2" instead of "eraser_1× 5 · eraser_3× 2".
  const fmtMix = (mix, prefix) => {
    if (!mix || !Object.keys(mix).length) return '—';
    return Object.entries(mix)
      .sort()
      .map(([k, v]) => `${k.replace(prefix, '')}× ${v}`)
      .join(' · ');
  };
  const eraserMixSub = fmtMix(p.eraser_bundle_mix, 'eraser_');
  const fiftyfiftyMixSub = fmtMix(p.fiftyfifty_bundle_mix, 'ff_');
  return `
    ${rangeHeader(p.range, '/admin/metrics/powerups', 'powerups')}
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'Eraser revenue',
        value: formatKobo(t.eraser_revenue_kobo || 0),
        sub: `${(t.eraser_units_sold || 0).toLocaleString('en-NG')} units sold`,
      })}
      ${statCard({
        label: '50/50 revenue',
        value: formatKobo(t.fiftyfifty_revenue_kobo || 0),
        sub: `${(t.fiftyfifty_units_sold || 0).toLocaleString('en-NG')} units sold`,
      })}
      ${statCard({
        label: 'Eraser bundle mix',
        value: eraserMixSub,
        tone: 'muted',
      })}
      ${statCard({
        label: '50/50 bundle mix',
        value: fiftyfiftyMixSub,
        tone: 'muted',
      })}
      ${statCard({
        label: 'Eraser arm rate',
        value: formatPercentBps(t.eraser_arm_rate_bps),
        sub: 'games where erasers brought',
      })}
      ${statCard({
        label: 'Eraser use rate',
        value: formatPercentBps(t.eraser_use_rate_bps),
        sub: 'among armed games',
      })}
      ${statCard({
        label: 'Erasers used',
        value: (t.erasers_used_total || 0).toLocaleString('en-NG'),
      })}
      ${statCard({
        label: '50/50s used',
        value: (t.fiftyfifty_used_total || 0).toLocaleString('en-NG'),
        sub: `avg ${((t.avg_fiftyfifty_per_live_game_x100 || 0) / 100).toFixed(2)}/game`,
      })}
      ${statCard({
        label: '50/50s granted',
        value: (
          (t.fiftyfifty_granted_referral || 0) +
          (t.fiftyfifty_granted_awarded || 0) +
          (t.fiftyfifty_granted_admin || 0)
        ).toLocaleString('en-NG'),
        sub: `ref ${t.fiftyfifty_granted_referral || 0} · rank ${t.fiftyfifty_granted_awarded || 0} · admin ${t.fiftyfifty_granted_admin || 0}`,
      })}
      ${statCard({
        label: 'Winrate delta',
        value: t.powerup_winrate_delta_bps == null
          ? 'n/a'
          : formatPercentBps(t.powerup_winrate_delta_bps),
        tone: 'muted',
        sub: t.powerup_winrate_delta_reason || '',
      })}
    </div>
    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Holders (point-in-time)
    </h4>
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'Players holding ≥1 eraser',
        value: formatPercentBps(t.powerup_holders_eraser_bps),
        sub: `${(t.outstanding_eraser_balance || 0).toLocaleString('en-NG')} units outstanding`,
        tone: 'muted',
      })}
      ${statCard({
        label: 'Players holding ≥1 50/50',
        value: formatPercentBps(t.powerup_holders_fiftyfifty_bps),
        sub: `${(t.outstanding_fiftyfifty_balance || 0).toLocaleString('en-NG')} units outstanding`,
        tone: 'muted',
      })}
    </div>
    <div style="margin-top:14px;">
      ${miniTrend(p.timeseries, 'eraser_units_sold', { label: 'Eraser units sold' })}
    </div>
    ${dataTable(p.timeseries, [
      'date',
      'eraser_units_sold',
      'eraser_revenue_kobo',
      'fiftyfifty_units_sold',
      'fiftyfifty_revenue_kobo',
      'erasers_used_total',
      'fiftyfifty_used_total',
    ])}
  `;
}

function renderFriends(f) {
  const t = f.totals;
  return `
    ${rangeHeader(f.range, '/admin/metrics/friends', 'friends')}
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'Friend requests sent',
        value: (t.friend_requests_sent || 0).toLocaleString('en-NG'),
      })}
      ${statCard({
        label: 'Friend requests accepted',
        value: (t.friendships_accepted || 0).toLocaleString('en-NG'),
        sub: `${formatPercentBps(t.friend_request_accept_rate_bps)} acceptance`,
      })}
      ${statCard({
        label: 'Referrals joined',
        value: (t.referrals_joined || 0).toLocaleString('en-NG'),
      })}
      ${statCard({
        label: 'Referrals rewarded',
        value: (t.referrals_rewarded || 0).toLocaleString('en-NG'),
        sub: `${formatPercentBps(t.referral_conversion_bps)} conversion`,
      })}
      ${statCard({
        label: 'Referral invites sent',
        value: t.referral_invites_sent == null ? 'n/a' : t.referral_invites_sent,
        tone: 'muted',
        sub: t.referral_invites_sent_reason || '',
      })}
    </div>
    <div style="margin-top:14px;">
      ${miniTrend(f.timeseries, 'referrals_rewarded', { label: 'Referrals rewarded' })}
    </div>
    ${dataTable(f.timeseries, [
      'date',
      'friend_requests_sent',
      'friendships_accepted',
      'referrals_joined',
      'referrals_rewarded',
    ])}
  `;
}

function renderEarnings(e) {
  const t = e.totals;
  const c = e.cohort || {};
  return `
    ${rangeHeader(e.range, '/admin/metrics/earnings', 'earnings')}
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'Net revenue',
        value: formatKobo(t.net_revenue_kobo || 0),
        tone: 'ok',
        sub: 'rake + power-up − fees',
      })}
      ${statCard({
        label: 'GGR (rake)',
        value: formatKobo(t.ggr_rake_kobo || 0),
        sub: `take ${formatPercentBps(t.take_rate_bps)}`,
      })}
      ${statCard({
        label: 'Power-up revenue',
        value: formatKobo(t.powerup_revenue_kobo || 0),
      })}
      ${statCard({
        label: 'Payment fees',
        value: formatKobo(t.payment_fees_kobo || 0),
        tone: 'muted',
      })}
      ${statCard({
        label: 'ARPPU',
        value: formatKobo(t.arppu_kobo || 0),
        sub: `${(t.paying_players || 0).toLocaleString('en-NG')} paying players`,
      })}
      ${statCard({
        label: 'Paying-player %',
        value: formatPercentBps(t.paying_player_pct_bps),
        sub: `of ${(t.active_players || 0).toLocaleString('en-NG')} active`,
      })}
      ${statCard({
        label: 'Handle (stakes)',
        value: formatKobo(t.duel_handle_kobo || 0),
        sub: `prizes ${formatKobo(t.duel_prizes_paid_kobo || 0)}`,
      })}
      ${statCard({
        label: 'Power-up liability',
        value: `${(t.outstanding_eraser_balance || 0).toLocaleString('en-NG')} + ${(t.outstanding_fiftyfifty_balance || 0).toLocaleString('en-NG')}`,
        sub: 'erasers + 50/50s held',
        tone: 'muted',
      })}
    </div>
    <h4 style="font-size:13px;text-transform:uppercase;letter-spacing:0.08em;color:var(--color-text-muted);margin:14px 0 8px;">
      Referred vs organic (revenue cohort)
    </h4>
    <div class="grid cols-2" style="gap:10px;">
      ${statCard({
        label: 'Referred avg revenue',
        value: formatKobo(c.referred_avg_revenue_kobo || 0),
        sub: `${(c.referred_users || 0).toLocaleString('en-NG')} users · ${formatKobo(c.referred_revenue_kobo || 0)} total`,
      })}
      ${statCard({
        label: 'Organic avg revenue',
        value: formatKobo(c.organic_avg_revenue_kobo || 0),
        sub: `${(c.organic_users || 0).toLocaleString('en-NG')} users · ${formatKobo(c.organic_revenue_kobo || 0)} total`,
      })}
    </div>
    <div style="margin-top:14px;">
      ${miniTrend(e.timeseries, 'duel_rake_kobo', {
        tone: 'money',
        formatter: (v) => formatKobo(v),
        label: 'Rake collected',
      })}
    </div>
    ${dataTable(e.timeseries, [
      'date',
      'duel_rake_kobo',
      'duel_handle_kobo',
      'duel_prizes_paid_kobo',
      'eraser_revenue_kobo',
      'payment_fees_kobo',
    ])}
  `;
}
